import { useCallback, useMemo, useRef, useState } from "react";

import { Box, Button, FormHelperText, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { indentWithTab } from "@codemirror/commands";
import { html } from "@codemirror/lang-html";
import { indentUnit } from "@codemirror/language";
import { EditorView, keymap } from "@codemirror/view";
import CodeMirror from "@uiw/react-codemirror";

interface HtmlCodeEditorProps {
    value?: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    label?: string;
    placeholder?: string;
    required?: boolean;
    error?: boolean;
}

const resolveModule = <T,>(
    module: T,
): T extends { default: infer DefaultExport } ? DefaultExport : T => {
    const moduleWithDefault = module as T & { default?: unknown };

    return (moduleWithDefault.default ?? module) as T extends {
        default: infer DefaultExport;
    }
        ? DefaultExport
        : T;
};

const HtmlCodeEditor = ({
    value = "",
    onChange,
    onBlur,
    label,
    placeholder,
    required = false,
    error = false,
}: HtmlCodeEditorProps) => {
    const theme = useTheme();
    const [isFormatting, setIsFormatting] = useState(false);
    const [formatError, setFormatError] = useState<string | null>(null);
    const valueRef = useRef(value);
    const onChangeRef = useRef(onChange);
    const isFormattingRef = useRef(false);

    valueRef.current = value;
    onChangeRef.current = onChange;

    const handleChange = (nextValue: string) => {
        valueRef.current = nextValue;
        setFormatError(null);
        onChange(nextValue);
    };

    const handleFormat = useCallback(async () => {
        const currentValue = valueRef.current;

        if (!currentValue.trim() || isFormattingRef.current) {
            return;
        }

        isFormattingRef.current = true;
        setIsFormatting(true);
        setFormatError(null);

        try {
            const [
                prettier,
                htmlPlugin,
                babelPlugin,
                estreePlugin,
                postcssPlugin,
            ] = await Promise.all([
                import("prettier/standalone.js"),
                import("prettier/plugins/html.js"),
                import("prettier/plugins/babel.js"),
                import("prettier/plugins/estree.js"),
                import("prettier/plugins/postcss.js"),
            ]);

            const prettierApi = resolveModule(prettier);
            const formattedValue = await prettierApi.format(currentValue, {
                parser: "html",
                plugins: [
                    resolveModule(htmlPlugin),
                    resolveModule(babelPlugin),
                    resolveModule(estreePlugin),
                    resolveModule(postcssPlugin),
                ],
                tabWidth: 4,
                useTabs: false,
            });

            valueRef.current = formattedValue;
            onChangeRef.current(formattedValue);
        } catch {
            setFormatError(
                "Formatting failed. Fix invalid HTML and try again.",
            );
        } finally {
            isFormattingRef.current = false;
            setIsFormatting(false);
        }
    }, []);

    const extensions = useMemo(
        () => [
            html({
                autoCloseTags: true,
                matchClosingTags: true,
            }),
            indentUnit.of("    "),
            keymap.of([
                indentWithTab,
                {
                    key: "Mod-s",
                    preventDefault: true,
                    run: () => {
                        void handleFormat();
                        return true;
                    },
                },
            ]),
            EditorView.lineWrapping,
        ],
        [handleFormat],
    );

    return (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                mb={1}
            >
                {label && (
                    <Typography
                        component="label"
                        variant="body2"
                        fontWeight={500}
                    >
                        {label}
                        {required && " *"}
                    </Typography>
                )}
                <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    disabled={!value.trim() || isFormatting}
                    onClick={handleFormat}
                >
                    {isFormatting ? "Formatting..." : "Format"}
                </Button>
            </Stack>

            <Box
                sx={{
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: error ? "error.main" : "divider",
                    borderRadius: 2,
                    transition: "border-color 0.2s, box-shadow 0.2s",
                    "&:hover": {
                        borderColor: error ? "error.main" : "text.primary",
                    },
                    "&:focus-within": {
                        borderColor: error ? "error.main" : "primary.main",
                        boxShadow: (currentTheme) =>
                            `0 0 0 1px ${
                                error
                                    ? currentTheme.palette.error.main
                                    : currentTheme.palette.primary.main
                            }`,
                    },
                    "& .cm-editor": {
                        fontSize: "0.875rem",
                    },
                    "& .cm-scroller": {
                        fontFamily:
                            '"Fira Code", Monaco, "Courier New", monospace',
                    },
                    "& .cm-focused": {
                        outline: "none",
                    },
                }}
            >
                <CodeMirror
                    value={value}
                    minHeight="320px"
                    maxHeight="640px"
                    theme={theme.palette.mode}
                    extensions={extensions}
                    basicSetup={{
                        lineNumbers: true,
                        foldGutter: true,
                        highlightActiveLineGutter: true,
                        highlightActiveLine: true,
                        bracketMatching: true,
                        closeBrackets: true,
                        autocompletion: true,
                        indentOnInput: true,
                    }}
                    placeholder={placeholder}
                    aria-label={label || "Custom HTML code editor"}
                    onChange={handleChange}
                    onBlur={onBlur}
                />
            </Box>

            {formatError && (
                <FormHelperText error>{formatError}</FormHelperText>
            )}
        </Box>
    );
};

export default HtmlCodeEditor;
